import { useState } from "react";
import styles from "../HealthChat.module.css";
import { href, useNavigate } from "react-router-dom";
const SYMPTOM_CATS = [
  {
    id: "irregular_periods",
    label: "Irregular Periods",
    emoji: "🔷",
    subOptions: [
      { id: "missed_periods", label: "Missed periods", icon: "📅" },
      { id: "frequent_periods", label: "Frequent periods", icon: "🔄" },
      { id: "spotting", label: "Spotting between periods", icon: "🩸" },
      { id: "heavy_flow", label: "Heavy menstrual bleeding", icon: "💧" },
      { id: "late_cycles", label: "Late or unpredictable cycles", icon: "⏳" },
    ],
  },
  {
    id: "pelvic",
    label: "Pelvic Pain",
    emoji: "🧬",
    subOptions: [
      {
        id: "severe_pain",
        label: "Severe pelvic or abdominal pain",
        icon: "🩺",
      },
      {
        id: "heavy_bleeding",
        label: "Heavy bleeding or passing large clots",
        icon: "🩸",
      },
      { id: "fainting", label: "Fainting or extreme dizziness", icon: "💡" },
      { id: "fever", label: "Fever with pelvic pain", icon: "🌡️" },
      {
        id: "pregnancy_bleeding",
        label: "Pregnancy with bleeding or severe pain",
        icon: "🤰",
      },
    ],
  },
  {
    id: "hair_loss",
    label: "Hair Loss / Thinning",
    emoji: "💇",
    subOptions: [
      { id: "sudden_hair_loss", label: "Sudden hair fall", icon: "💇" },
      { id: "patchy_loss", label: "Patchy hair loss", icon: "🪮" },
      { id: "thinning_hair", label: "Hair becoming thinner", icon: "✨" },
      { id: "receding_hairline", label: "Receding hairline", icon: "📉" },
    ],
  },
  {
    id: "hair_growth",
    label: "Excess Hair Growth",
    emoji: "🧑",
    subOptions: [
      { id: "facial_hair", label: "Facial hair growth", icon: "🙂" },
      { id: "chest_hair", label: "Chest or stomach hair", icon: "🧬" },
      { id: "rapid_growth", label: "Rapid increase in body hair", icon: "⚡" },
      { id: "dark_coarse_hair", label: "Dark coarse hair growth", icon: "🖤" },
    ],
  },
  {
    id: "acne",
    label: "Acne / Skin Issues",
    emoji: "😶",
    subOptions: [
      { id: "jawline_acne", label: "Jawline or chin acne", icon: "🙂" },
      { id: "oily_skin", label: "Very oily skin", icon: "✨" },
      { id: "painful_acne", label: "Painful cystic acne", icon: "🔴" },
      { id: "skin_darkening", label: "Dark patches on skin", icon: "🟤" },
    ],
  },
  {
    id: "weight_gain",
    label: "Weight Gain",
    emoji: "⚖️",
    subOptions: [
      { id: "rapid_weight_gain", label: "Rapid weight gain", icon: "⚖️" },
      { id: "belly_fat", label: "Increased belly fat", icon: "⭕" },
      {
        id: "difficulty_losing",
        label: "Difficulty losing weight",
        icon: "📉",
      },
      { id: "constant_bloating", label: "Constant bloating", icon: "🎈" },
    ],
  },
];

const STEP_CONFIG = {
  concerns: {
    title: "Hi, I'm your Care Navigator",
    subtitle: "What are you experiencing today?",
    previous: null,
  },
  questions: {
    title: "Symptom Check",
    subtitle: "Do you have any of the following?",
    previous: "concerns",
  },
  details: {
    title: "Understanding Symptoms",
    subtitle: "Let me understand your symptoms better",
    previous: "questions",
  },
  extraQuestions: {
    title: "History & Cycle",
    subtitle: "A few more questions for better guidance",
    previous: "details",
  },
  summary: {
    title: "Review Information",
    subtitle: "Please review before continuing",
    previous: "extraQuestions",
  },
  results: {
    title: "Assessment Found",
    subtitle: "Based on your responses",
    previous: "summary",
  },
  actions: {
    title: "Next Steps",
    subtitle: "Recommended Actions",
    previous: "results",
  },
};

export default function SymptomPickerScreen({ onSelect, onBack }) {
  const [step, setStep] = useState("concerns");
  const [activeSubOptions, setActiveSubOptions] = useState(null);
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(4);
  const [selected, setSelected] = useState({});
  const navigate = useNavigate();

  const [symptomDetails, setSymptomDetails] = useState({
    duration: "",
    severity: "",
    frequency: "",
  });

  const [extraDetails, setExtraDetails] = useState({
    cycleLength: "",
    additionalSymptoms: [],
  });

  const currentStep = STEP_CONFIG[step];

  // Common Back Logic
  const handleGoBack = () => {
    if (currentStep.previous) {
      setStep(currentStep.previous);
    } else {
      onBack();
    }
  };

  const getSelectedLabels = () => {
    return Object.entries(selected)
      .filter(([_, val]) => val === "yes")
      .map(
        ([id]) => activeSubOptions?.subOptions.find((o) => o.id === id)?.label,
      )
      .filter(Boolean)
      .join(", ");
  };

  const filtered = SYMPTOM_CATS.filter((c) =>
    c.label.toLowerCase().includes(search.toLowerCase()),
  );
  const visibleItems = filtered.slice(0, visibleCount);
  const [questionVisibleCount, setQuestionVisibleCount] = useState(2);

  return (
    <div className={styles.symptomPickerScreen}>
      {/* --- COMMON HEADER SECTION --- */}
      <button className={styles.selectDoctorBack} onClick={handleGoBack}>
        ← Back
      </button>

      <div className={styles.symptomPickerBotRow}>
        <img
          src="/images/chatbot.svg"
          alt="bot"
          width={48}
          height={48}
          className={styles.symptomPickerBotImg}
        />
        <div
          className={`${styles.symptomPickerBotBubble} ${styles.CBbodyText}`}
        >
          <strong>{currentStep.title}</strong>
          <p style={{ fontSize: "0.85rem", margin: 0, opacity: 0.9 }}>
            {currentStep.subtitle}
          </p>
        </div>
      </div>
      {/* ------------------------------ */}

      {/* STEP 1: CONCERNS */}
      {step === "concerns" && (
        <div className="mt-4">
          <div className={styles.selectCareSearchWrap}>
            <span className={styles.selectCareSearchIcon}>🔍</span>
            <input
              className={styles.selectCareSearch}
              placeholder="Search your concerns..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <div className={styles.selectCareDropdown}>
                {SYMPTOM_CATS.filter((s) =>
                  s.label.toLowerCase().includes(search.toLowerCase()),
                ).map((s) => (
                  <div
                    key={s.id}
                    className={styles.selectCareDropdownItem}
                    onClick={() => {
                      setActiveSubOptions(s);

                      setQuestionVisibleCount(2);
                      setStep("questions");
                      setSearch("");
                    }}
                  >
                    <span>{s.emoji}</span>
                    <span>{s.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-center gap-2 my-2">
            <div className="text-gray-400">or</div>
            <div className="h-px w-10 bg-gray-300" />
            <div className="text-sm font-medium text-gray-600">
              Common Concerns
            </div>
            <div className="h-px w-10 bg-gray-300" />
          </div>

          <div className={styles.symptomPickerGrid}>
            {filtered.map((cat) => (
              <div
                key={cat.id}
                className={styles.symptomPickerCard}
                onClick={() => {
                  setActiveSubOptions(cat);
                  setStep("questions");
                }}
              >
                <span className={styles.symptomPickerEmoji}>{cat.emoji}</span>
                <div className={styles.symptomPickerCardLabel}>{cat.label}</div>
              </div>
            ))}
          </div>

          {/* {visibleCount < filtered.length && (
            <div className="flex justify-center mt-2">
              <button
                className={`${styles.symptomPickerOther} ${styles.CBBtnFonstSize}`}
                onClick={() =>
                  setVisibleCount((prev) => Math.min(prev + 4, filtered.length))
                }
              >
                Load More ({filtered.length - visibleCount})
              </button>
            </div>
          )} */}
        </div>
      )}

      {/* STEP 2: QUESTIONS */}
      {step === "questions" && activeSubOptions && (
        <div className={styles.questionWrapper}>
          <div className="mb-4 text-xs text-gray-500 uppercase tracking-wide">
            Selected: {activeSubOptions.label}
          </div>
          {activeSubOptions.subOptions
            .map((item) => (
              <div key={item.id} className={styles.questionCard}>
                <div className={styles.questionLeft}>
                  <div className={styles.questionIcon}>{item.icon}</div>
                  <div className={styles.questionText}>{item.label}</div>
                </div>
                <div className={styles.questionActions}>
                  <button
                    className={`${styles.questionBtn} ${selected[item.id] === "no" ? styles.questionBtnActive : ""}`}
                    onClick={() =>
                      setSelected((prev) => ({ ...prev, [item.id]: "no" }))
                    }
                  >
                    No
                  </button>
                  <button
                    className={`${styles.questionBtn} ${selected[item.id] === "yes" ? styles.questionBtnYes : ""}`}
                    onClick={() =>
                      setSelected((prev) => ({ ...prev, [item.id]: "yes" }))
                    }
                  >
                    Yes
                  </button>
                </div>
              </div>
            ))}

          {/* {questionVisibleCount < activeSubOptions.subOptions.length && (
            <div className="flex justify-center">
              <button
                className={`${styles.symptomPickerOther} ${styles.CBBtnFonstSize}`}
                onClick={() =>
                  setQuestionVisibleCount((prev) =>
                    Math.min(prev + 2, activeSubOptions.subOptions.length),
                  )
                }
              >
                Load More (
                {activeSubOptions.subOptions.length - questionVisibleCount})
              </button>
            </div>
          )} */}

          {/* {Object.keys(selected).length > 0 && ( */}
          <div className={styles.bottomActionBar}>
            <button
            disabled={Object.keys(selected).length==0}
              className={styles.visitModelContinueBtn}
              onClick={() => setStep("details")}
            >
              Continue
            </button>
            </div>
          {/* )} */}
        </div>
      )}

      {/* STEP 3: DETAILS */}
      {step === "details" && (
        <div className={styles.detailsWrapper}>
          <div className={styles.detailsSection}>
            <div className={styles.detailsQuestion}>
              How long have you been experiencing this?
            </div>
            <div className={styles.optionGrid}>
              {[
                "Less than 24 hrs",
                "1-3 days",
                "3-6 days",
                "more than 6 days",
              ].map((item) => (
                <button
                  key={item}
                  className={`${styles.optionBtn} ${symptomDetails.duration === item ? styles.optionBtnActive : ""}`}
                  onClick={() =>
                    setSymptomDetails((prev) => ({ ...prev, duration: item }))
                  }
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.detailsSection}>
            <div className={styles.detailsQuestion}>
              How would you rate the severity?
            </div>
            <div className={styles.severityRow}>
              {[0, 1, 2, 3, 4].map((num) => (
                <button
                  key={num}
                  className={`${styles.severityBtn} ${symptomDetails.severity === num ? styles.severityBtnActive : ""}`}
                  onClick={() =>
                    setSymptomDetails((prev) => ({ ...prev, severity: num }))
                  }
                >
                  <div className={styles.severityEmoji}>
                    {num <= 1
                      ? "😊"
                      : num === 2
                        ? "😐"
                        : num === 3
                          ? "☹️"
                          : "😣"}
                  </div>
                  <div>{num}</div>
                </button>
              ))}
            </div>
          </div>

          <div className={styles.detailsSection}>
            <div className={styles.detailsQuestion}>
              How often do you experience this?
            </div>
            <div className={styles.optionGrid}>
              {["Occasionally", "Sometimes", "Often", "Daily"].map((item) => (
                <button
                  key={item}
                  className={`${styles.optionBtn} ${symptomDetails.frequency === item ? styles.optionBtnActive : ""}`}
                  onClick={() =>
                    setSymptomDetails((prev) => ({ ...prev, frequency: item }))
                  }
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.bottomActionBar}>

          <button
            className={styles.visitModelContinueBtn}
            onClick={() => setStep("extraQuestions")}
          >
            Continue
          </button>
          </div>
        </div>
      )}

      {/* STEP 4: EXTRA QUESTIONS */}
      {step === "extraQuestions" && (
        <div className={styles.detailsWrapper}>
          <div className={styles.detailsSection}>
            <div className={styles.detailsQuestion}>
              Typical menstrual cycle length?
            </div>
            <div className={styles.optionGrid}>
              {[
                "Less than 21 days",
                "21 - 35 days",
                "More than 35 days",
                "Irregular / Varies",
              ].map((item) => (
                <button
                  key={item}
                  className={`${styles.optionBtn} ${extraDetails.cycleLength === item ? styles.optionBtnActive : ""}`}
                  onClick={() =>
                    setExtraDetails((prev) => ({ ...prev, cycleLength: item }))
                  }
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.detailsSection}>
            <div className={styles.detailsQuestion}>
              Do you also experience any of these?
            </div>
            <div className={styles.multiSelectGrid}>
              {[
                "Acne",
                "Excess hair growth",
                "Weight gain",
                "Thinning hair",
                "Dark patches",
                "Hair loss",
              ].map((item) => {
                const active = extraDetails.additionalSymptoms.includes(item);
                return (
                  <button
                    key={item}
                    className={`${styles.optionBtn} ${active ? styles.optionBtnActive : ""}`}
                    onClick={() => {
                      setExtraDetails((prev) => ({
                        ...prev,
                        additionalSymptoms: active
                          ? prev.additionalSymptoms.filter((s) => s !== item)
                          : [...prev.additionalSymptoms, item],
                      }));
                    }}
                  >
                    {item}
                  </button>
                );
              })}
            </div>
          </div>
          <div className={styles.bottomActionBar}>

          <button
            className={styles.visitModelContinueBtn}
            onClick={() => setStep("summary")}
          >
            Review Summary
          </button>
          </div>
        </div>
      )}

      {/* STEP 5: SUMMARY */}
      {step === "summary" && (
        <div className={styles.summaryContainer}>
          <div className={styles.summaryCard}>
            <h4 className={styles.summaryCardTitle}>Your concern summary</h4>
            <div className={styles.summaryList}>
              <div className={styles.summaryItem}>
                <label>Main Concerns</label>
                <span>{getSelectedLabels() || "None"}</span>
              </div>
              <div className={styles.summaryItem}>
                <label>Duration</label>
                <span>{symptomDetails.duration}</span>
              </div>
              <div className={styles.summaryItem}>
                <label>Severity</label>
                <span>
                  {symptomDetails.severity !== ""
                    ? `${symptomDetails.severity}/4`
                    : "N/A"}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <label>Cycle Length</label>
                <span>{extraDetails.cycleLength}</span>
              </div>
            </div>
            <button
              className={styles.summaryEditBtn}
              onClick={() => {
                setQuestionVisibleCount(2);
                setStep("questions");
              }}
            >
              ✏️ Edit responses
            </button>
          </div>
          <div className={styles.bottomActionBar}>

          <button
            className={styles.visitModelContinueBtn}
            onClick={() => setStep("results")}
          >
            Looks good, continue
          </button>
          </div>
        </div>
      )}

      {/* STEP 6: RESULTS */}
      {step === "results" && (
        <div className={styles.resultsContainer}>
          <div className={styles.resultsCard}>
            <div className={styles.resultsTable}>
              <div className={styles.resultsRow}>
                <span>Risk Level</span>
                <span className={styles.riskBadgeModerate}>Moderate</span>
              </div>
              <div className={styles.resultsRow}>
                <span>Possible Area</span>
                <span className={styles.textRight}>
                  Hormonal imbalance / PCOS risk
                </span>
              </div>
            </div>
            <div className={styles.diagnosisDisclaimer}>
              <span className={styles.infoIcon}>ⓘ</span>
              <p>This is not a diagnosis. Please consult a specialist.</p>
            </div>
          </div>
          <div className={styles.bottomActionBar}>
          
          <button
            className={styles.visitModelContinueBtn}
            onClick={() => setStep("actions")}
          >
            View recommended actions
          </button>
          </div>
        </div>
      )}

      {/* STEP 7: ACTIONS */}
      {step === "actions" && (
        <div className={styles.summaryContainer}>
          <div className="space-y-3">
            {[
              {
                title: "Start PCOS risk assessment",
                desc: "Short assessment",
                bg: "#F8F5FF",
                link: "/health-assessment",
                border:"#BB9DFF"

              },
              {
                title: "Book An Appointment",
                desc: "Consult a specialist",
                bg: "#F2FFFA",
                link: "/book-appointment",
                border:"#6EFFCB"
              },
              {
                title: "Chat with expert",
                desc: "Guidance and support",
                bg: "#EDF3FF",
                border:"#83ACFF"
              },
              {
                title: "Explore PCOS care program",
                desc: "Personalised programs for PCOS management",
                bg: "#FFFEED",
                border:"#FFF885"
              },
            ].map((action, idx) => (
              <div
                key={idx}
                className={styles.actionCard}
                style={{
                  backgroundColor: action.bg,
                  padding: "12px",
                  borderRadius: "12px",
                  border:`2px solid ${action.border}`,
                  display: "flex",
                  gap: "10px",
                  cursor: action.link ? "pointer" : "default",
                }}
                onClick={() => {
                  if (action.link) {
                    navigate(action.link);
                  }
                }}
              >
                <div className="flex-1">
                  <div className="font-bold text-sm">{action.title}</div>
                  <div className="text-xs text-gray-600">{action.desc}</div>
                </div>

                {action.link && (
                  <div className="flex items-center text-gray-500 text-lg">
                    →
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className={styles.bottomActionBar}>

          <button
            className={styles.visitModelContinueBtn}
            onClick={() =>
              onSelect({
                parent: activeSubOptions?.id,
                selected,
                symptomDetails,
                extraDetails,
              })
            }
          >
            Save & Finish
          </button>
          </div>
        </div>
      )}
    </div>
  );
}
